import {FormControl} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {always, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CustomerMessageRequestBody,
  useRenderCustomerMessageEmailTemplateMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {
  EmailWindow,
  type EmailFormValues as FormValues,
  handleApiError,
  isEmailHtmlEmpty,
  toEmailBodyPayload,
} from '@dms/shared';

import {useDebouncedCallback, useDeepCompareEffect} from 'shared/web';

import {useCertificateLink} from '../../DigitalCertificateLink/hooks/useCertificateLink';

interface EmailPreviewProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  serviceCaseId: string;
  inspectionId?: string;
}

const EMPTY_BODY = ' ';

export function EmailPreview(props: EmailPreviewProps) {
  const [getDigitalCertificateLink] = useCertificateLink(props.inspectionId);
  const [renderEmail, {isLoading: isLoadingEmail, originalArgs}] =
    useRenderCustomerMessageEmailTemplateMutation();

  const [htmlData, setHtmlData] = useState<string | null>(null);

  const [customerId, customerEmailAddress, customerMessageId, subject, body] = useWatch({
    control: props.control,
    name: ['customerId', 'customerEmailAddress', 'customerMessageId', 'subject', 'body'],
  });

  const isCustomerMissing = isNilOrEmpty(customerId) || isNilOrEmpty(customerEmailAddress);
  // A cleared editor holds `<p><br></p>`, which `isNilOrEmpty` reports as filled.
  const isEmailMissing = isNilOrEmpty(subject) && isEmailHtmlEmpty(body);

  const handleRenderEmail = () => {
    const {body, customerId, subject, customerMessageId} = props.formApi.getValues();

    if (
      customerMessageId?.[0] === 'customer-message' &&
      isNilOrEmpty(subject) &&
      isEmailHtmlEmpty(body)
    ) {
      setHtmlData(null);
      return;
    }

    // The preview must agree with the send path, and parsing the markup once is enough.
    const emailBody = toEmailBodyPayload(body);

    const payload = match<
      FormValues['customerMessageId'][number],
      CustomerMessageRequestBody['payload']
    >(customerMessageId[0])
      .with(
        'customer-message',
        always({
          body: emailBody.body || EMPTY_BODY,
          subject: subject || EMPTY_BODY,
        })
      )
      .with(
        'service-customer_check_in',
        always({
          serviceCaseId: props.serviceCaseId,
          digitalCertificateUrl: getDigitalCertificateLink(),
          attachments: [],
        })
      )
      .with(
        'service-customer_handover',
        always({
          serviceCaseId: props.serviceCaseId,
          digitalCertificateUrl: getDigitalCertificateLink(),
          attachments: [],
        })
      )
      .exhaustive();

    renderEmail({
      customerMessageId: customerMessageId[0],
      renderCustomerMessageRequestBody: {
        customerId,
        payload,
        customerMessageId: customerMessageId[0],
        bodyFormat: emailBody.bodyFormat,
      },
    })
      .unwrap()
      .then((data) => data.body)
      .then(setHtmlData)
      .catch(handleApiError);
  };

  const debouncedHandleRenderEmail = useDebouncedCallback(handleRenderEmail, 1000);

  useDeepCompareEffect(() => {
    const messageId = customerMessageId?.[0];
    const areEmailDataRequired = messageId === 'customer-message';
    const originalMessageId = originalArgs?.customerMessageId;
    const didMessageIdChanged =
      isNotNil(originalMessageId) && isNotNil(messageId) && originalMessageId !== messageId;

    const shouldRefetchEmailPreview = match([
      isCustomerMissing,
      areEmailDataRequired,
      isEmailMissing,
      didMessageIdChanged,
    ])
      .with([false, true, false, Pattern.boolean], always(true))
      .with([false, false, true, Pattern.boolean], always(true))
      .with([false, true, true, true], always(true))
      .otherwise(always(false));

    if (shouldRefetchEmailPreview) {
      debouncedHandleRenderEmail();
    } else {
      setHtmlData(null);
    }
  }, [customerId, customerEmailAddress, subject, body, customerMessageId]);

  return (
    <VStack height="100%">
      <Box position="relative" height="100%">
        <Show when={isNilOrEmpty(htmlData)}>
          <EmailPreviewButtonWrapper data-testid="customerEmailForm-email-window-empty-preview">
            <VStack align="center">
              <Text size="xSmall" align="center" color="tertiary">
                {i18n.t('entity.customer.labels.fillAllRequiredFields')}
              </Text>
            </VStack>
          </EmailPreviewButtonWrapper>
        </Show>
        <EmailWindow
          htmlData={htmlData}
          isRerendering={isLoadingEmail && isNotNilOrEmpty(htmlData)}
          data-testid="customerEmailForm-email-window"
        />
      </Box>
    </VStack>
  );
}

const EmailPreviewButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
